/* actions */
import { createAction, createReducer, on } from '@ngrx/store';
import * as AIAssistantActions from './ai-assistant.actions';
import * as AuthenticationActions from 'libs/authentication/src/lib/state/authentication.actions';
import { cloneDeep, orderBy } from 'lodash';
import { ProjectChannel, ProjectChannelMessageWithRelations } from '@rappider/rappider-sdk';
import { UnsynchronizedMessage } from '../utils/unsynchronized-message.interface';
import { v4 } from 'uuid';

/* state key */
export const featureKey = 'aiAssistant';

/* state interface */
export interface State {
  chatSessionId: string;
  isAssistantTyping: boolean;
  channels: ProjectChannel[] | undefined;
  channelMessages: Record<string, ProjectChannelMessageWithRelations[]> | undefined;
  analyzeMessages: any;
  messages: ProjectChannelMessageWithRelations[];
  isLoaded: boolean;
  isAiThinking?: boolean;
  unsynchronizedMessages: UnsynchronizedMessage[];
  isSenderMessageSendSuccessfully: boolean;
  error?: {
    error: any;
    key: string;
  };
}

export const initialState: State = {
  chatSessionId: '',
  isAssistantTyping: false,
  channels: undefined,
  channelMessages: undefined,
  analyzeMessages: undefined,
  messages: [],
  unsynchronizedMessages: [],
  isLoaded: false,
  isAiThinking: false,
  isSenderMessageSendSuccessfully: false,
  error: undefined
};

const UpdateAuthenticationTokenWithProjectId = createAction(AuthenticationActions.ActionTypes.UpdateAuthenticationTokenWithProjectId);
const Logout = createAction(AuthenticationActions.ActionTypes.Logout);

export const reducer = createReducer(
  initialState,

  on(AIAssistantActions.GetChannelMessagesByTimestampSuccessful, (state, action) => {
    // Combine the current messages and the new messages into one array.
    const publicGeneralChannelId = state.channels?.find(channel => channel.isDefault)?.id;
    // Determine the current channel ID based on the action payload.
    // If no channelId is provided, we assume it's a private conversation.
    // If the channelId is the public general channel, we use 'general' as the key.
    // Otherwise, we use the provided channelId directly.
    const currentChannelId = !action.payload?.channelId ? 'private' : (action.payload.channelId === publicGeneralChannelId ? 'general' : action.payload.channelId);
    const currentChannelMessages = state.channelMessages?.[currentChannelId] ?? [];
    const channelMessages = [
      ...currentChannelMessages,
      ...action.payload.channelMessages,
    ];

    // Use a Map to ensure each message ID is unique.
    // The Map constructor automatically handles de-duplication.
    const uniqueChannelMessages = new Map(
      channelMessages.map(message => [message.id, message])
    );

    return {
      ...state,
      // Convert the unique messages back to an array and sort them.
      channelMessages: {
        ...state.channelMessages,
        [currentChannelId]: orderBy(
          Array.from(uniqueChannelMessages.values()),
          ['createdDate'],
          'asc'
        ),
      },
    };
  }),

  on(AIAssistantActions.SetProjectChannels, (state, action) => {
    const generalChannelId = action.payload.channels?.find(channel => channel.isDefault)?.id;
    let generalChannelMessages: any[] = [];
    // if channel messages are loaded before the general channel is set, we need to set them
    if (generalChannelId && state.channelMessages && !state.channelMessages['general']) {
      generalChannelMessages = orderBy(
        [
          ...(state.channelMessages['general'] ?? []),
          ...state.channelMessages[generalChannelId] ?? []
        ],
        ['createdDate'],
        'asc'
      );
    }

    return {
      ...state,
      channels: action.payload.channels,
      channelMessages: {
        ...state.channelMessages,
        'general': generalChannelMessages
      }
    };
  }),

  on(AIAssistantActions.RealtimeTypingCompleted, (state, action) => {
    const currentChannelId = action.payload.channelId || 'private'; // Default to 'private' if no channelId is provided
    const currentChannelMessages = cloneDeep(state.channelMessages?.[currentChannelId]) ?? [];
    if (currentChannelMessages.length) {
      currentChannelMessages[currentChannelMessages.length - 1].animated = true;
    }
    return {
      ...state,
      // Convert the unique messages back to an array and sort them.
      channelMessages: {
        ...state.channelMessages,
        [currentChannelId]: currentChannelMessages
      },
    };
  }),

  on(AIAssistantActions.SendDirectMessage, (state, action) => {
    const currentChannelId = 'private';
    const currentChannelMessages = state.channelMessages?.[currentChannelId] ?? [];
    // Create the optimistic message with the temporary ID
    const currentUserMessage = {
      ...action.payload.message,
      id: action.payload.tempId,
      createdDate: new Date().toISOString(),
    };

    const channelMessages = [...currentChannelMessages, currentUserMessage];
    return {
      ...state,
      channelMessages: {
        ...state.channelMessages,
        [currentChannelId]: channelMessages,
      },
      isAiThinking: true,
    };
  }),

  on(AIAssistantActions.AnalyzeDataSource, (state, action) => {
    const currentChannelId = 'private';
    const currentChannelMessages = state.channelMessages?.[currentChannelId] ?? [];
    const currentUserMessage = {
      aiFunctionId: "ragsearch",
      status: "new",
      type: "direct-message",
      metadata: [],
      receiverId: "00000000-0000-0000-0000-000000000001",
      message: action.payload.message,
      data: { message: action.payload.message },
      temporary: true,
      createdDate: new Date().toISOString(),
      id: v4(),
    } as any;

    const channelMessages = [...currentChannelMessages, currentUserMessage];
    return {
      ...state,
      channelMessages: {
        ...state.channelMessages,
        [currentChannelId]: channelMessages,
      },
      isAiThinking: true,
    };
  }),

  on(AIAssistantActions.ResetSenderMessageStatus, (state) => {
    return {
      ...state,
      isSenderMessageSendSuccessfully: false
    };
  }),

  on(AIAssistantActions.AnalyzeDataSourceSuccessful, (state, action) => {
    const currentChannelId = 'private';
    const messages = [...state.channelMessages['private']];
    const index = (messages as any).map((item, i) => ({ ...item, i }))
      .filter(item => item.temporary)
      .reduce((prev, current) => new Date(prev.createdDate) > new Date(current.createdDate) ? prev : current)
      .i;
    messages[index] = { ...messages[index], correlationId: action.payload.correlationId };
    return {
      ...state,
      channelMessages: {
        ...state.channelMessages,
        [currentChannelId]: messages
      },
      isAiThinking: true,
      isSenderMessageSendSuccessfully: true
    };
  }),

  on(AIAssistantActions.SendDirectMessageSuccessful, (state, action) => {
    const currentChannelId = 'private';
    const currentChannelMessages = state.channelMessages?.[currentChannelId] ?? [];

    // Find the index of the temporary message using its tempId
    const tempMessageIndex = currentChannelMessages.findIndex(
      (m) => m.id === action.payload.tempId
    );

    const newMessages = [...currentChannelMessages];

    if (tempMessageIndex !== -1) {
      // If the temporary message is found, replace it with the real one from the server
      newMessages[tempMessageIndex] = action.payload.userMessage;
      // Add the AI's message
      if (action.payload.aiMessage) {
        newMessages.push(action.payload.aiMessage);
      }
    } else {
      // Fallback in case the temp message isn't found (should not happen)
      newMessages.push(action.payload.userMessage);
      if (action.payload.aiMessage) {
        newMessages.push(action.payload.aiMessage);
      }
    }

    return {
      ...state,
      channelMessages: {
        ...state.channelMessages,
        [currentChannelId]: newMessages,
      },
      isAiThinking: false,
    };
  }),

  on(AIAssistantActions.GetAnalyzeMessagesSuccessful, (state, action) => ({
    ...state,
    analyzeMessages: action.payload.analyzeMessages
  })),
  on(AIAssistantActions.AnalyzeDataSourceFailure, (state, action) => {
    const errorMessage = {
      createdDate: new Date().toISOString(),
      status: 'completed',
      identifier: 'data-source-analyze',
      data: {
        message: action.payload.message,
        summarized: [
          {
            result: action.payload.error?.error?.error?.message,
            date: new Date().toISOString(),
            level: 1
          }
        ]
      }
    }
    return {
      ...state,
      analyzeMessages: [errorMessage, ...state.analyzeMessages]
    }
  }),

  on(AIAssistantActions.Error, (state, action) => ({
    ...state,
    error: action.payload.error
  })),

  on(UpdateAuthenticationTokenWithProjectId, () => (initialState)),
  on(Logout, () => (initialState))
);
